import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Armchair, ArrowRight, Info, Plane } from 'lucide-react';
import SeatMap from '@/components/seat/SeatMap';
import { findSeatMapsForFlight } from '@/api/functions';
import { createPageUrl } from '@/utils';
import { getCityName } from '@/components/airportUtils';
import { format, parseISO } from 'date-fns';

/**
 * "Select your seats" step between Booking Options and Review & Pay.
 *
 * The customer's flight comes from the SerpAPI / Google Flights search, which
 * has no real Duffel offer. To show a live seat map we re-search Duffel
 * server-side for the same slice and match an offer by the exact flight-number
 * chain (POST /api/seat-maps/find-for-flight). The matched offer's seat maps
 * power the existing SeatMap picker.
 *
 * Silent fallback is the contract: if the flight can't be matched on Duffel,
 * has no seat maps, or anything errors/times out, we forward straight to
 * Review with the exact state FlightOptions gave us (plus `seatSelections`,
 * which Review already accepts). Checkout is never blocked by this page.
 */

// Duffel re-search involves offer polling, so allow a generous-but-bounded wait.
const AVAILABILITY_CHECK_TIMEOUT_MS = 25000;

// Staged loader copy — advances on a timer to give a sense of progress while
// the server matches the flight on Duffel and probes fare candidates.
const LOADING_STAGES = [
  'Matching your flight with the airline…',
  'Comparing fare options…',
  'Retrieving the live cabin layout…',
  'Checking which seats are open…',
];
const STAGE_PROGRESS = [30, 55, 75, 90];

// Stable reference: SeatMap resets its internal selection state whenever the
// initialSelections prop identity changes, so never pass an inline `{}`.
const EMPTY_SELECTIONS = {};

// A seat map is only worth showing if at least one seat can be selected.
function seatMapHasAvailability(seatMap) {
  return (seatMap?.cabins || []).some((cabin) =>
    (cabin.rows || []).some((row) =>
      (row.sections || []).some((section) =>
        (section.elements || []).some((el) =>
          el.type === 'seat' && Array.isArray(el.available_services) && el.available_services.length > 0
        )
      )
    )
  );
}

function parseFlightNumbers(slice) {
  return (slice.segments || []).map((segment) => {
    const rawNumber = String(segment.marketing_carrier_flight_number || '');
    return {
      carrier: segment.marketing_carrier?.iata_code || (rawNumber.match(/^[A-Z0-9]{2}/i) || [''])[0],
      number: (rawNumber.match(/\d+/) || [''])[0],
    };
  });
}

export default function SelectSeats() {
  const location = useLocation();
  const navigate = useNavigate();
  const baseState = location.state;

  // entries: [{ offer, segment, mergedIndex, seatMaps }] — offer/segment are
  // the matched Duffel ones; mergedIndex points into Review's seat order.
  const [selectableSegments, setSelectableSegments] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingStage, setLoadingStage] = useState(0);
  const [showSkip, setShowSkip] = useState(false);
  const [unavailable, setUnavailable] = useState(false); // brief notice before redirecting
  // Legs with NO seat maps while other legs have them — shown as a notice so
  // customers don't think their return/outbound went missing.
  const [missingLegs, setMissingLegs] = useState([]);
  const seatSelectionsRef = useRef([]); // indexed by merged segment order, matching Review
  const navigatedRef = useRef(false);

  const goToReview = useCallback((replace = false) => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    navigate(createPageUrl('Review'), {
      state: {
        ...baseState,
        seatSelections: seatSelectionsRef.current,
      },
      replace,
    });
  }, [navigate, baseState]);

  // Loader pacing: advance the stage copy and reveal the skip link.
  useEffect(() => {
    if (selectableSegments || unavailable) return;
    const stageTimer = setInterval(
      () => setLoadingStage((s) => Math.min(s + 1, LOADING_STAGES.length - 1)),
      3200
    );
    const skipTimer = setTimeout(() => setShowSkip(true), 3000);
    return () => {
      clearInterval(stageTimer);
      clearTimeout(skipTimer);
    };
  }, [selectableSegments, unavailable]);

  useEffect(() => {
    if (!baseState?.selectedFlight) {
      navigate(createPageUrl('Search'), { replace: true });
      return;
    }

    let cancelled = false;

    // Seat selection isn't possible — show a short "not offered" notice so the
    // hand-off to Review reads as intentional rather than a broken redirect.
    const exitGracefully = () => {
      if (cancelled || navigatedRef.current) return;
      setUnavailable(true);
      setTimeout(() => {
        if (!cancelled) goToReview(true);
      }, 2000);
    };

    const checkAvailability = async () => {
      const { selectedFlight, returnFlight, searchParams } = baseState;

      // Build legs (one per slice) in the same merged order Review uses for
      // seatSelections: selectedFlight's slices, then the return offer's
      // slices when the return is a separate object.
      const legs = [];
      let mergedIndex = 0;
      const collectSlices = (flight) => {
        (flight?.slices || []).forEach((slice) => {
          const segments = slice.segments || [];
          if (segments.length === 0) return;
          legs.push({
            slice,
            segmentMergedIndexes: segments.map(() => mergedIndex++),
          });
        });
      };
      collectSlices(selectedFlight);
      if (returnFlight?.slices && returnFlight !== selectedFlight && returnFlight.id !== selectedFlight.id) {
        collectSlices(returnFlight);
      }

      seatSelectionsRef.current = Array(mergedIndex).fill(null).map(() => ({}));

      if (legs.length === 0) {
        goToReview(true);
        return;
      }

      const passengers = baseState.passengers?.length ? baseState.passengers : [{ type: 'adult' }];
      const cabinClass = searchParams?.cabin_class || 'economy';
      // Fare brand the customer actually clicked on Booking Options (e.g.
      // "Main Plus"). Duffel returns one offer per fare brand for the same
      // cabin (fare_brand_name on the slice), each with its own seat pricing —
      // a Basic Economy customer must never see Main Cabin's free seats. The
      // server only considers brand-matching offers (strict); if the booked
      // brand isn't sold on Duffel, seat selection silently skips to Review,
      // which is this page's normal fallback contract.
      const bookingOptionMain = baseState.bookingOption?.together
        || baseState.bookingOption?.departing
        || baseState.bookingOption?.returning;
      const fareBrand = bookingOptionMain?.option_title || null;

      const lookupLeg = async (leg) => {
        const segments = leg.slice.segments;
        const flightNumbers = parseFlightNumbers(leg.slice);
        if (flightNumbers.some((fn) => !fn.carrier || !fn.number)) return null;

        try {
          const { data, status } = await findSeatMapsForFlight({
            origin: segments[0].origin?.iata_code,
            destination: segments[segments.length - 1].destination?.iata_code,
            departure_date: (segments[0].departing_at || '').slice(0, 10),
            cabin_class: cabinClass,
            passengers: passengers.map((p) => ({ type: p.type || 'adult' })),
            flight_numbers: flightNumbers,
            fare_brand: fareBrand,
            strict_fare_brand: true,
            // Provider-prefixed tokens (e.g. `mondee:opt:v1:`) let the
            // server route the seat-map lookup back to the offer's own
            // source instead of the Duffel re-search.
            booking_token: baseState.bookingToken
              || returnFlight?.booking_token
              || selectedFlight?.booking_token
              || null,
          });
          if (status === 200 && data?.matched && data.offer && Array.isArray(data.seat_maps)) {
            return { offer: data.offer, seatMaps: data.seat_maps };
          }
          // Server short-circuited (airline has no Duffel seat-map support) —
          // the response was instant, so the caller can jump straight to
          // Review without the "not offered" interstitial.
          if (status === 200 && data?.skipped) {
            return { skipped: true };
          }
        } catch {
          // Treat as unmatched
        }
        return null;
      };

      let legResults;
      try {
        legResults = await Promise.race([
          Promise.all(legs.map(lookupLeg)),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), AVAILABILITY_CHECK_TIMEOUT_MS)),
        ]);
      } catch {
        exitGracefully();
        return;
      }

      // Pair each matched Duffel segment with its merged index. The server
      // matched the offer on the exact flight-number chain, so segment order
      // and count line up with the SerpAPI slice.
      const selectable = [];
      legs.forEach((leg, legIdx) => {
        const result = legResults[legIdx];
        if (!result?.offer) return;
        const duffelSegments = result.offer.slices?.[0]?.segments || [];
        const selectableSegmentIds = new Set(
          result.seatMaps
            .filter((sm) => sm.segment_id && seatMapHasAvailability(sm))
            .map((sm) => sm.segment_id)
        );

        duffelSegments.forEach((duffelSegment, segIdx) => {
          if (!selectableSegmentIds.has(duffelSegment.id)) return;
          const targetMergedIndex = leg.segmentMergedIndexes[segIdx];
          if (targetMergedIndex === undefined) return;
          selectable.push({
            offer: result.offer,
            segment: duffelSegment,
            mergedIndex: targetMergedIndex,
            seatMaps: result.seatMaps,
          });
        });
      });

      if (cancelled) return;

      if (selectable.length === 0) {
        // Every leg was an instant carrier-level skip — the customer barely
        // saw the loader, so go straight to Review with no interstitial.
        // Only show the "not offered" notice when a real Duffel search ran
        // (i.e. they actually watched the loading animation for a while).
        if (legResults.every((r) => r?.skipped)) {
          goToReview(true);
          return;
        }
        exitGracefully();
        return;
      }

      // Partial coverage: tell the customer which leg(s) have no seat map so
      // a missing return/outbound reads as an airline limitation, not a bug.
      const selectableMergedIndexes = new Set(selectable.map((s) => s.mergedIndex));
      const legsWithoutMaps = legs
        .map((leg, idx) => ({ leg, idx }))
        .filter(({ leg }) => !leg.segmentMergedIndexes.some((mi) => selectableMergedIndexes.has(mi)))
        .map(({ leg, idx }) => {
          const segs = leg.slice.segments;
          const o = segs[0]?.origin?.iata_code;
          const d = segs[segs.length - 1]?.destination?.iata_code;
          return {
            name: legs.length === 2 ? (idx === 0 ? 'outbound flight' : 'return flight') : `flight ${idx + 1}`,
            route: o && d ? `${o} → ${d}` : null,
          };
        });
      setMissingLegs(legsWithoutMaps);

      setSelectableSegments(selectable);
    };

    checkAvailability();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSegmentSeatsSelected = (selections) => {
    const entry = selectableSegments[currentStep];
    seatSelectionsRef.current[entry.mergedIndex] = selections || {};

    if (currentStep + 1 < selectableSegments.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      goToReview();
    }
  };

  const handleBack = () => {
    if (currentStep === 0) return;
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Seat selection requires at least one passenger slot
  const passengers = baseState?.passengers?.length ? baseState.passengers : [{ type: 'adult' }];

  // Seat selection isn't offered — brief notice, then on to Review & Pay.
  if (unavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--bg-primary, #0a0a0a)' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="text-center max-w-sm"
        >
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center">
            <Armchair className="w-6 h-6 text-white/40" />
          </div>
          <p className="text-white/85 font-light text-lg">Seat selection isn't offered for this flight</p>
          <p className="text-white/60 text-sm font-normal mt-2">
            You can still choose seats when you check in with the airline. Taking you to Review &amp; Pay…
          </p>
          <div className="w-40 h-0.5 mx-auto mt-6 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className="h-full bg-white/40 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 1.9, ease: 'linear' }}
            />
          </div>
        </motion.div>
      </div>
    );
  }

  // Availability check in progress
  if (!selectableSegments) {
    const firstSliceSegments = baseState?.selectedFlight?.slices?.[0]?.segments || [];
    const loaderOrigin = firstSliceSegments[0]?.origin?.iata_code;
    const loaderDest = firstSliceSegments[firstSliceSegments.length - 1]?.destination?.iata_code;
    const cabinCols = [0, 1, 2, null, 3, 4, 5];
    const cabinRows = [0, 1, 2, 3];

    return (
      <div className="min-h-screen flex items-start justify-center pt-[min(16vh,7rem)] sm:pt-[18vh] px-4" style={{ backgroundColor: 'var(--bg-primary, #0a0a0a)' }}>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          {loaderOrigin && loaderDest && (
            <div className="flex items-center justify-center gap-2.5 text-white/60 text-sm font-normal mb-7">
              <span>{getCityName(loaderOrigin) || loaderOrigin}</span>
              <span className="flex items-center gap-1 text-brand-400/70">
                <span className="w-6 h-px bg-gradient-to-r from-transparent to-brand-400/50" />
                <Plane className="w-3.5 h-3.5" />
                <span className="w-6 h-px bg-gradient-to-l from-transparent to-brand-400/50" />
              </span>
              <span>{getCityName(loaderDest) || loaderDest}</span>
            </div>
          )}

          {/* Mini fuselage: seats pulse in a diagonal wave while a scan beam sweeps the cabin */}
          <div className="relative w-44 mx-auto rounded-t-[2.75rem] rounded-b-xl border border-white/10 bg-white/[0.02] px-3 pt-7 pb-3.5 overflow-hidden">
            <motion.div
              className="absolute inset-x-0 h-10 bg-gradient-to-b from-transparent via-brand-400/10 to-transparent pointer-events-none"
              animate={{ top: ['-15%', '105%'] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
            />
            {cabinRows.map((r) => (
              <div key={r} className="flex justify-center gap-1 mb-1.5 last:mb-0">
                {cabinCols.map((c, idx) =>
                  c === null ? (
                    <div key={`aisle-${idx}`} className="w-3.5" />
                  ) : (
                    <motion.div
                      key={idx}
                      className="w-4 h-4 rounded-t-[5px] rounded-b-[2px] border border-brand-400/30"
                      animate={{
                        opacity: [0.25, 1, 0.25],
                        backgroundColor: [
                          'rgba(10, 18, 51,0.10)',
                          'rgba(56,189,248,0.40)',
                          'rgba(10, 18, 51,0.10)',
                        ],
                      }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: r * 0.18 + c * 0.07 }}
                    />
                  )
                )}
              </div>
            ))}
          </div>

          <p className="text-white/85 font-normal mt-7">Checking seat availability</p>

          {/* Staged progress: copy rotates and the bar advances per stage */}
          <div className="h-5 mt-1.5">
            <AnimatePresence mode="wait">
              <motion.p
                key={loadingStage}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35 }}
                className="text-white/45 text-sm font-normal"
              >
                {LOADING_STAGES[loadingStage]}
              </motion.p>
            </AnimatePresence>
          </div>
          <div className="w-56 h-1 mx-auto mt-4 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-brand-500/80 to-brand-300/80"
              initial={{ width: '8%' }}
              animate={{ width: `${STAGE_PROGRESS[loadingStage]}%` }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
            />
          </div>

          <div className="h-10 mt-6">
            <AnimatePresence>
              {showSkip && (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  onClick={() => goToReview(true)}
                  className="flex items-center gap-1.5 mx-auto text-sm text-white/40 hover:text-white/85 font-normal transition-colors"
                >
                  <span className="underline underline-offset-2 decoration-white/25">Skip seat selection</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    );
  }

  const entry = selectableSegments[currentStep];
  const segment = entry.segment;
  const originCode = segment.origin?.iata_code;
  const destinationCode = segment.destination?.iata_code;
  const departingAt = segment.departing_at;

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: 'var(--bg-primary, #0a0a0a)' }}>
      <div className="max-w-3xl mx-auto space-y-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
          <div className="flex items-center gap-2 text-white/60 text-sm font-normal">
            <Armchair className="w-4 h-4" />
            <span>Flight {currentStep + 1} of {selectableSegments.length}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-light text-white">Select your seats</h1>
          <p className="text-white/85 text-sm font-normal">
            {getCityName(originCode) || originCode} ({originCode}) → {getCityName(destinationCode) || destinationCode} ({destinationCode})
            {segment.marketing_carrier?.name ? ` • ${segment.marketing_carrier.name}` : ''}
            {segment.marketing_carrier_flight_number ? ` ${segment.marketing_carrier_flight_number}` : ''}
            {segment.aircraft?.name ? ` • ${segment.aircraft.name}` : ''}
            {departingAt ? ` • ${format(parseISO(departingAt), 'MMM d, yyyy')}` : ''}
          </p>
        </motion.div>

        {missingLegs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="flex items-start gap-2.5 rounded-2xl border border-brand-400/20 bg-brand-500/[0.07] px-4 py-3"
          >
            <Info className="w-4 h-4 mt-0.5 text-brand-300/80 flex-shrink-0" />
            <p className="text-[13px] leading-relaxed text-white/65 font-normal">
              Seat selection isn't offered for your{' '}
              <span className="text-white/85">
                {missingLegs.map((m) => `${m.name}${m.route ? ` (${m.route})` : ''}`).join(' and ')}
              </span>
              {' '}— the airline doesn't provide a live seat map for{' '}
              {missingLegs.length > 1 ? 'those legs' : 'that leg'}. Those seats will be assigned at
              check-in, or you can set a seat preference on the next page and our concierge will request them.
            </p>
          </motion.div>
        )}

        <SeatMap
          key={segment.id}
          flightOffer={entry.offer}
          segment={segment}
          passengers={passengers}
          onSeatsSelected={handleSegmentSeatsSelected}
          onBack={currentStep > 0 ? handleBack : null}
          initialSelections={seatSelectionsRef.current[entry.mergedIndex] ?? EMPTY_SELECTIONS}
          preloadedSeatMapData={entry.seatMaps}
        />

        <div className="flex justify-center pb-8">
          <button
            type="button"
            onClick={() => goToReview()}
            className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white/90 font-normal transition-colors"
          >
            <span className="underline underline-offset-2 decoration-white/30">Skip seat selection</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
